/**
 * IconComponent — A lightweight wrapper for Iconoir SVGs.
 *
 * Usage: <jt-icon icon="send-solid"></jt-icon>
 *
 * Tree-shaking: Vite only includes the SVGs imported below in the final bundle.
 */
import SendSolid from 'iconoir/icons/solid/send.svg?raw';
import Quote from 'iconoir/icons/regular/quote.svg?raw';
import Expand from 'iconoir/icons/regular/expand.svg?raw';
import Xmark from 'iconoir/icons/regular/xmark.svg?raw';
import NavArrowDown from 'iconoir/icons/regular/nav-arrow-down.svg?raw';
import NavArrowUp from 'iconoir/icons/regular/nav-arrow-up.svg?raw';
import iconStylesText from "./IconComponent.css?inline";

// Registry of supported icons
const ICONS: Record<string, string> = {
    'send-solid': SendSolid,
    'quote': Quote,
    'expand': Expand,
    'cancel': Xmark,
    'nav-arrow-down': NavArrowDown,
    'nav-arrow-up': NavArrowUp,
};

const iconSheet = new CSSStyleSheet();
iconSheet.replaceSync(iconStylesText);

export class IconComponent extends HTMLElement {
    /**
     * Specifies which attributes should be monitored for changes.
     */
    static get observedAttributes(): string[] {
        return ["icon"];
    }

    constructor() {
        super();
    }

    /**
     * Called when the element is first connected to the document's DOM.
     */
    connectedCallback(): void {
        if (!this.shadowRoot) {
            this.attachShadow({ mode: "open" });
            this.shadowRoot!.adoptedStyleSheets = [iconSheet];
        }
        this.render();
    }

    /**
     * Called when one of the observed attributes changes.
     */
    attributeChangedCallback(name: string, _oldValue: string | null, _newValue: string | null): void {
        if (name === "icon") {
            this.render();
        }
    }

    /**
     * Renders the SVG content based on the 'icon' attribute safely using DOMParser.
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const iconName = this.getAttribute("icon");
        const svgContent = iconName ? ICONS[iconName] : null;

        // Clear existing content
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.removeChild(this.shadowRoot.firstChild);
        }

        if (svgContent) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgContent, "image/svg+xml");
            const svgElement = doc.documentElement;
            
            // Ensure it's indeed an SVG and not a parsing error
            if (svgElement && svgElement.tagName.toLowerCase() === 'svg') {
                // Import the node into the current document context
                const importedSvg = document.importNode(svgElement, true);
                this.shadowRoot.appendChild(importedSvg);
            }
        }
    }
}

/**
 * Register the custom element if it hasn't been registered yet.
 */
if (!customElements.get("jt-icon")) {
    customElements.define("jt-icon", IconComponent);
}
